import type { App } from './App';
import type { ClassProfile, Student } from '../types';
import {
  getProfiles,
  saveProfile,
  deleteProfile,
  setActiveProfileId,
  createProfile,
  updateStudents,
} from '../storage/profiles';
import { parseAndCreateStudents } from '../utils/parser';
import { exportStudentsCSV, importCSVAsProfile } from '../utils/csv';
import { uuid } from '../utils/helpers';

type Panel = 'list' | 'edit' | 'new';

export class ClassModal {
  private el: HTMLElement;
  private app: App;
  private panel: Panel = 'list';
  private editingProfile: ClassProfile | null = null;

  constructor(container: HTMLElement, app: App) {
    this.app = app;
    this.el = document.createElement('div');
    this.el.className = 'class-modal-wrap';
    container.appendChild(this.el);
    this.renderList();
  }

  private renderList() {
    this.panel = 'list';
    const profiles = getProfiles();
    const active = this.app.getActiveProfile();

    this.el.innerHTML = `
      <div class="class-modal">
        <div class="modal-header">
          <h3>내 학급 관리</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          ${profiles.length === 0 ? `<p class="empty-msg">아직 등록한 학급이 없어요.</p>` : ''}
          <div class="profile-list">
            ${profiles.map((p) => `
              <div class="profile-item ${p.id === active?.id ? 'active' : ''}">
                <div class="profile-info" data-id="${p.id}">
                  <span class="profile-name">${p.name}</span>
                  <span class="profile-meta">${p.students.length}명</span>
                  ${p.id === active?.id ? '<span class="badge-active">현재</span>' : ''}
                </div>
                <div class="profile-actions">
                  <button class="btn-sm" data-action="use" data-id="${p.id}">선택</button>
                  <button class="btn-sm" data-action="edit" data-id="${p.id}">편집</button>
                  <button class="btn-sm btn-danger" data-action="delete" data-id="${p.id}">삭제</button>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-sm" id="btn-new-class">+ 새 학급 만들기</button>
          <label class="btn-sm btn-import">
            📥 CSV 가져오기
            <input type="file" id="file-import" accept=".csv" style="display:none" />
          </label>
        </div>
      </div>
    `;

    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.app.closeOverlay());
    this.el.querySelector('#btn-new-class')?.addEventListener('click', () => this.renderEdit(null));

    this.el.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = (btn as HTMLElement).dataset.action;
        const id = (btn as HTMLElement).dataset.id!;
        if (action === 'use') {
          setActiveProfileId(id);
          this.renderList();
        } else if (action === 'edit') {
          const p = getProfiles().find((pr) => pr.id === id) ?? null;
          this.renderEdit(p);
        } else if (action === 'delete') {
          if (confirm(`"${getProfiles().find((p) => p.id === id)?.name}" 학급을 삭제할까요?`)) {
            deleteProfile(id);
            this.renderList();
          }
        }
      });
    });

    const fileInput = this.el.querySelector<HTMLInputElement>('#file-import');
    fileInput?.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      const profile = await importCSVAsProfile(file);
      if (profile) {
        saveProfile(profile);
        setActiveProfileId(profile.id);
        this.renderList();
      } else {
        alert('CSV 파일을 읽지 못했어요. 파일 형식을 확인해주세요.');
      }
    });
  }

  private renderEdit(profile: ClassProfile | null) {
    this.panel = 'edit';
    this.editingProfile = profile;
    const students = profile?.students ?? [];
    const namesText = students.map((s) => s.name).join('\n');

    this.el.innerHTML = `
      <div class="class-modal">
        <div class="modal-header">
          <button class="btn-back-sm" id="btn-back">← 목록</button>
          <h3>${profile ? profile.name + ' 편집' : '새 학급 만들기'}</h3>
          <button class="btn-icon-sm" id="btn-close">✕</button>
        </div>
        <div class="modal-body">
          <label class="form-label">학급 이름</label>
          <input class="form-input" id="class-name" type="text"
            value="${profile?.name ?? ''}"
            placeholder="예) 3학년 2반" />

          <label class="form-label">
            학생 명단
            <span class="label-hint">(줄바꿈·쉼표·탭 구분 모두 가능, 이름*2로 동명이인)</span>
          </label>
          <textarea class="form-textarea" id="student-names" rows="12"
            placeholder="김철수&#10;이영희&#10;박민수&#10;(또는 한 줄에 여러 명: 김철수, 이영희, 박민수)">${namesText}</textarea>

          <div class="parsed-preview" id="parsed-preview">
            <span id="preview-count">${students.length > 0 ? `${students.length}명 등록됨` : ''}</span>
          </div>
        </div>
        <div class="modal-footer">
          ${profile ? `<button class="btn-sm" id="btn-export-csv">📤 CSV 내보내기</button>` : ''}
          <button class="btn-primary-sm" id="btn-save">저장</button>
        </div>
      </div>
    `;

    const nameInput = this.el.querySelector<HTMLInputElement>('#class-name')!;
    const textarea = this.el.querySelector<HTMLTextAreaElement>('#student-names')!;
    const preview = this.el.querySelector<HTMLElement>('#preview-count')!;

    textarea.addEventListener('input', () => {
      const parsed = parseAndCreateStudents(textarea.value);
      preview.textContent = parsed.length > 0 ? `${parsed.length}명 인식됨` : '';
    });

    this.el.querySelector('#btn-back')?.addEventListener('click', () => this.renderList());
    this.el.querySelector('#btn-close')?.addEventListener('click', () => this.app.closeOverlay());

    this.el.querySelector('#btn-save')?.addEventListener('click', () => {
      const name = nameInput.value.trim();
      if (!name) { nameInput.focus(); return; }
      const students = parseAndCreateStudents(textarea.value);

      if (profile) {
        profile.name = name;
        updateStudents(profile.id, students);
        const updated = getProfiles().find((p) => p.id === profile.id);
        if (updated) { updated.name = name; saveProfile(updated); }
      } else {
        const newProfile = createProfile(name, students);
        setActiveProfileId(newProfile.id);
      }

      this.renderList();
    });

    this.el.querySelector('#btn-export-csv')?.addEventListener('click', () => {
      if (profile) exportStudentsCSV(profile.students, profile.name);
    });
  }

  destroy() {
    this.el.remove();
  }
}
